import type { ButtonHTMLAttributes, ReactNode } from "react";

type Variante = "primario" | "secundario" | "fantasma" | "destrutivo";
type Tamanho = "sm" | "md" | "lg";

const VARIANTES: Record<Variante, string> = {
  primario: "bg-acao text-acao-contraste hover:bg-acao-viva",
  secundario: "bg-superficie text-texto border border-borda hover:border-acao",
  fantasma: "bg-transparent text-texto-suave hover:bg-canvas hover:text-texto",
  destrutivo: "bg-carmim text-white hover:opacity-90",
};

const TAMANHOS: Record<Tamanho, string> = {
  sm: "h-8 px-3 text-caption",
  md: "h-10 px-4 text-body",
  lg: "h-12 px-6 text-body-lg",
};

interface Props extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className"> {
  variante?: Variante;
  tamanho?: Tamanho;
  carregando?: boolean;
  larguraTotal?: boolean;
  children: ReactNode;
}

export function Botao({
  variante = "primario",
  tamanho = "md",
  carregando = false,
  larguraTotal = false,
  disabled,
  children,
  ...resto
}: Props) {
  const inativo = disabled === true || carregando;

  return (
    <button
      {...resto}
      disabled={inativo}
      // `aria-busy` porque o rótulo não muda durante o envio: trocar o texto
      // do botão faz o leitor de tela anunciar uma ação diferente da que a
      // pessoa acionou.
      aria-busy={carregando}
      className={[
        "inline-flex items-center justify-center gap-2 rounded-md font-texto font-medium",
        "transition-[background-color,border-color,opacity] duration-150",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
        "focus-visible:outline-acao-viva",
        "disabled:cursor-not-allowed disabled:opacity-55",
        larguraTotal ? "w-full" : "",
        VARIANTES[variante],
        TAMANHOS[tamanho],
      ].join(" ")}
    >
      {carregando && (
        <span
          aria-hidden="true"
          className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent motion-reduce:animate-none"
        />
      )}
      {children}
    </button>
  );
}
